/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

const createPageList = (
  currentPage: number,
  dataPerPage: number,
  totalPages: number,
) => {
  const result = [];
  const firstIndex =
    Math.floor((currentPage - 1) / dataPerPage) * dataPerPage + 1;
  const lastIndex = Math.min(firstIndex + dataPerPage - 1, totalPages);

  for (let i = firstIndex; i <= lastIndex; i++) {
    result.push(i);
  }

  return result;
};

export const usePagination = (
  currentPage: number,
  totalPages: number,
  maxPageNum = 10,
) => {
  const [numList, setNumList] = useState<number[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const pageNumList = createPageList(currentPage, maxPageNum, totalPages);

  const isDisabledPrev = useMemo(() => {
    return currentPage - 1 < maxPageNum;
  }, [currentPage]);

  const isDisabledNext = useMemo(() => {
    return (
      Math.floor((currentPage + maxPageNum - 1) / maxPageNum) >=
      Math.floor(totalPages / maxPageNum)
    );
  }, [currentPage, maxPageNum, totalPages]);

  const handlePrevPage = useCallback(() => {
    if (currentPage === 1) return;

    const prevPageNum = Math.max(
      maxPageNum * Math.floor((currentPage - maxPageNum - 1) / maxPageNum) + 1,
      1,
    );

    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: String(prevPageNum),
    });
  }, [currentPage, maxPageNum, setSearchParams, searchParams]);

  const handleNextPage = useCallback(() => {
    if (currentPage === totalPages) return;

    const nextPageNum = Math.min(
      Math.floor((currentPage + maxPageNum - 1) / maxPageNum) * maxPageNum + 1,
      totalPages,
    );

    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: String(nextPageNum),
    });
  }, [currentPage, maxPageNum, searchParams, totalPages]);

  const handleClickNum = useCallback(
    (idx: number) => () => {
      setSearchParams({
        ...Object.fromEntries(searchParams),
        page: String(idx),
      });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    setNumList(pageNumList);
  }, [currentPage, totalPages]);

  return {
    handlePrevPage,
    handleNextPage,
    handleClickNum,
    numList,
    isDisabledPrev,
    isDisabledNext,
  };
};
